// components/ResultadoContent.jsx
import React, { useState } from "react";
import Swal from "sweetalert2";
import FoodTab from "./ResultadoTabs/FoodTab";
import TradeTab from "./ResultadoTabs/TradeTab";
import LandUseTab from "./ResultadoTabs/LandUseTab";
import GHGTab from "./ResultadoTabs/GHGTab";
import NitrogenTab from "./ResultadoTabs/NitrogenTab";
import BiodiversityTab from "./ResultadoTabs/BiodiversityTab";

export default function ScenathonCharts({ resultsData, scenathonId, pathwayName: selectedPathwayName }) {
  const subTabs = [
    { key: "Targets", label: "Targets" },
    { key: "Food", label: "Food Security" },
    { key: "Trade", label: "Trade" },
    { key: "Land use", label: "Land Use" },
    { key: "GHG", label: "GHG" },
    { key: "Nitrogen", label: "Nitrogen" },
    { key: "Biodiversity", label: "Biodiversity" },
  ];

  const validTabKeys = new Set(subTabs.map((tab) => tab.key));

  const getInitialSubTab = () => {
    if (typeof window === "undefined") return "Targets";
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("scenathonTab");
    return requested && validTabKeys.has(requested) ? requested : "Targets";
  };

  const [activeSubTab, setActiveSubTab] = useState(getInitialSubTab);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (activeSubTab === "Targets") {
      params.delete("scenathonTab");
    } else {
      params.set("scenathonTab", activeSubTab);
    }

    const search = params.toString();
    const newUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }, [activeSubTab]);

  const pickSeries = (block) => {
    if (Array.isArray(block)) return block;
    if (!block || typeof block !== "object") return [];

    const preferred = ["Global", "Average of all countries"];
    for (const key of preferred) {
      if (Array.isArray(block[key])) return block[key];
    }

    const values = Object.values(block);
    const directArray = values.find((v) => Array.isArray(v));
    if (directArray) return directArray;

    return [];
  };

  const extractRegionSeries = (block) => {
    if (!block || typeof block !== "object") return [];
    if (Array.isArray(block)) return [block];

    const excludedKeys = new Set(["Global", "Average of all countries"]);
    return Object.entries(block)
      .filter(([key, value]) => !excludedKeys.has(key) && Array.isArray(value))
      .map(([, value]) => value);
  };

  const getLastYearRow = (series) => {
    if (!Array.isArray(series) || series.length === 0) return null;

    let best = null;
    for (const row of series) {
      const year = Number(row?.year);
      if (!Number.isFinite(year)) continue;
      if (!best || year > Number(best.year)) {
        best = row;
      }
    }

    return best || series[series.length - 1] || null;
  };

  const asNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  // Robustly extract the pathway node if it's wrapped in an extra object/array
  const resolvePathwayNode = (data, preferName) => {
    if (!data || typeof data !== "object") return [null, null];

    const root = Array.isArray(data) ? data[0] : data;
    if (!root) return [null, null];

    // Priority: If the preferred name matches a key in root that has charts, use it
    if (preferName && root[preferName] && Array.isArray(root[preferName]?.charts)) {
      return [preferName, root[preferName]];
    }

    // Attempt 1: The keys of root are the pathway names (Backend /results logic)
    const entries = Object.entries(root);
    if (entries.length > 0) {
      // If the first entry HAS a charts array, it's likely the pathway node
      if (Array.isArray(entries[0][1]?.charts)) {
        return entries[0];
      }

      // Attempt 2: If the first entry is itself an object containing a pathway name key...
      // This handles cases where resultsData might be [{ "Sustainability": { ... } }]
      const firstEntryValue = entries[0][1];
      if (typeof firstEntryValue === "object") {
        const subEntries = Object.entries(firstEntryValue);
        if (subEntries.length > 0 && Array.isArray(subEntries[0][1]?.charts)) {
          return subEntries[0];
        }
      }
    }

    return [null, null];
  };

  const [pathwayName, pathwayObject] = resolvePathwayNode(resultsData, selectedPathwayName);
  const chartData = pathwayObject?.charts?.[0] ?? {};

  const foodBlock = chartData?.Food?.["Daily per capita food intake"];
  const foodSeries = pickSeries(foodBlock);
  const foodLastRow = getLastYearRow(foodSeries);
  const foodActual = asNumber(foodLastRow?.feasibleKcal);
  const foodMinTarget = asNumber(foodLastRow?.minTarget);
  const foodMaxTarget = asNumber(foodLastRow?.maxTarget);

  const getFoodSecurityEvaluation = (block) => {
    const regionEntries = Array.isArray(block)
      ? [["Global", block]]
      : Object.entries(block || {}).filter(
        ([key, value]) =>
          key !== "Global" &&
          key !== "Average of all countries" &&
          Array.isArray(value),
      );

    const regionRows = regionEntries
      .map(([key, series]) => ({ key, lastRow: getLastYearRow(series) }))
      .filter(({ lastRow }) => lastRow && lastRow.year != null);

    if (regionRows.length > 0) {
      const failures = regionRows
        .map(({ key, lastRow }) => {
          const actual = asNumber(lastRow?.feasibleKcal);
          const minTarget = asNumber(lastRow?.minTarget);
          const maxTarget = asNumber(lastRow?.maxTarget);
          const meets =
            actual != null &&
            minTarget != null &&
            maxTarget != null &&
            actual >= minTarget &&
            actual <= maxTarget;

          return {
            key,
            actual,
            minTarget,
            maxTarget,
            year: lastRow?.year,
            meets,
          };
        })
        .filter(({ meets }) => !meets);

      return { status: failures.length === 0 ? "Achieved" : "Not achieved", failures };
    }

    if (foodActual != null && foodMinTarget != null && foodMaxTarget != null) {
      const meets = foodActual >= foodMinTarget && foodActual <= foodMaxTarget;
      return {
        status: meets ? "Achieved" : "Not achieved",
        failures: meets
          ? []
          : [
            {
              key: "Global",
              actual: foodActual,
              minTarget: foodMinTarget,
              maxTarget: foodMaxTarget,
              year: foodLastRow?.year,
              meets,
            },
          ],
      };
    }

    return { status: "No target defined", failures: [] };
  };

  const foodSecurityEvaluation = getFoodSecurityEvaluation(foodBlock);
  const foodSecurityStatus = foodSecurityEvaluation.status;
  const foodSecurityFailures = foodSecurityEvaluation.failures;

  const showFoodSecurityFailuresModal = () => {
    if (foodSecurityFailures.length === 0) return;

    const html = foodSecurityFailures
      .map(({ key, actual, minTarget, maxTarget, year }) => {
        if (actual == null || minTarget == null || maxTarget == null) {
          return `<li><strong>${key}</strong>: missing target or actual value.</li>`;
        }

        return `<li><strong>${key}</strong> (${year}): ${actual.toLocaleString("en-US", { maximumFractionDigits: 0 })} kcal/cap/day outside the range ${minTarget.toLocaleString("en-US", { maximumFractionDigits: 0 })} - ${maxTarget.toLocaleString("en-US", { maximumFractionDigits: 0 })} kcal/cap/day</li>`;
      })
      .join("");

    Swal.fire({
      icon: "warning",
      title: "Food Security - Not achieved",
      width: "700px",
      html: `<style>
    .food-security-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 0.75rem;
    }

    .food-security-item {
      background: #fff5f5;
      border: 1px solid #FADBD8;
      border-radius: 12px;
      padding: 1rem;
      color: #721c24;
      font-size: 0.94rem;
      line-height: 1.6;
      text-align: left;
    }

    @media (max-width: 900px) {
      .food-security-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 600px) {
      .food-security-grid {
        grid-template-columns: 1fr;
      }
    }
  </style>

  <div style="text-align:left; font-family: Arial, sans-serif; color: #212121;">
    <p style="margin:0 0 0.75rem; font-size:0.97rem; line-height:1.6; font-weight:600;">
      The following regions/countries are outside the target range:
    </p>

    <div class="food-security-grid">
      ${foodSecurityFailures
          .map(({ key, actual, minTarget, maxTarget, year }) => {
            if (actual == null || minTarget == null || maxTarget == null) {
              return `
              <div class="food-security-item">
                <strong>${key}</strong>: missing target or actual value.
              </div>
            `;
            }

            return `
            <div class="food-security-item">
              <strong>${key}</strong> (${year}): 
              ${actual.toLocaleString("en-US", { maximumFractionDigits: 0 })} 
              kcal/cap/day outside the range 
              (${minTarget.toLocaleString("en-US", { maximumFractionDigits: 0 })} - 
              ${maxTarget.toLocaleString("en-US", { maximumFractionDigits: 0 })} 
              kcal/cap/day)
            </div>
          `;
          })
          .join("")}
    </div>
  </div>`,
      confirmButtonText: "Close",
    });
  };

  const ghgSeries = pickSeries(chartData?.GHG?.["GHG by sector"]);
  const ghgYear2050 = Array.isArray(ghgSeries)
    ? ghgSeries.find((r) => Number(r?.year) === 2050) || null
    : null;
  const ghgLastRow = ghgYear2050 || getLastYearRow(ghgSeries);
  const ghgActual = asNumber(ghgLastRow?.netAfolu);
  const ghgTarget = asNumber(ghgLastRow?.target) ?? (Number(ghgLastRow?.year) === 2050 ? 4000 : null);
  const ghgDeforestation = asNumber(ghgLastRow?.deforestation);
  const ghgSequestration = asNumber(ghgLastRow?.sequestration);
  const ghgDeforestationSequestrationSum =
    ghgDeforestation != null && ghgSequestration != null
      ? ghgDeforestation + ghgSequestration
      : null;
  const ghgDeforestationSequestrationGt =
    ghgDeforestationSequestrationSum != null
      ? ghgDeforestationSequestrationSum / 1000
      : null;
  const ghgNetAfolu2025to2050 = Array.isArray(ghgSeries)
    ? ghgSeries.reduce((sum, row) => {
      const year = Number(row?.year);
      const value = asNumber(row?.netAfolu);
      return sum + (Number.isFinite(year) && year >= 2025 && year <= 2050 && value != null ? value : 0);
    }, 0)
    : null;
  const ghgNetAfolu2025to2050Gt =
    ghgNetAfolu2025to2050 != null ? ghgNetAfolu2025to2050 / 1000 : null;

  const landUseAreaBlock = chartData?.["Land use"]?.["Area by landcover"];
  const landUseAreaSeries = pickSeries(landUseAreaBlock);
  const landUseArea2020Row = Array.isArray(landUseAreaSeries)
    ? landUseAreaSeries.find((row) => Number(row.year) === 2020)
    : null;
  const landUseArea2050Row = Array.isArray(landUseAreaSeries)
    ? landUseAreaSeries.find((row) => Number(row.year) === 2050)
    : null;
  const totalArea2020 = landUseArea2020Row
    ? ["forest", "cropland", "pasture", "otherLand", "urban"].reduce(
      (sum, key) => sum + (asNumber(landUseArea2020Row[key]) || 0),
      0,
    )
    : null;
  const totalArea2050 = landUseArea2050Row
    ? ["forest", "cropland", "pasture", "otherLand", "urban"].reduce(
      (sum, key) => sum + (asNumber(landUseArea2050Row[key]) || 0),
      0,
    )
    : null;

  const protectedAreaBlock = chartData?.Biodiversity?.["Evolution of protected areas"];
  const protectedAreaSeries = pickSeries(protectedAreaBlock);
  const protectedArea2020Row = Array.isArray(protectedAreaSeries)
    ? protectedAreaSeries.find((row) => Number(row.year) === 2020)
    : null;
  const protectedArea2050Row = Array.isArray(protectedAreaSeries)
    ? protectedAreaSeries.find((row) => Number(row.year) === 2050)
    : null;
  const protectedArea2020Share =
    protectedArea2020Row && totalArea2020
      ? (asNumber(protectedArea2020Row.forest) || 0) +
      (asNumber(protectedArea2020Row.otherLands) || 0) +
      (asNumber(protectedArea2020Row.miscellaneous) || 0)
      : null;
  const protectedArea2050Share =
    protectedArea2050Row && totalArea2050
      ? ((asNumber(protectedArea2050Row.forest) || 0) +
        (asNumber(protectedArea2050Row.otherLands) || 0) +
        (asNumber(protectedArea2050Row.miscellaneous) || 0)) /
      totalArea2050
      : null;

  const biodiversityShareBlock =
    chartData?.Biodiversity?.["Land where natural processes predominate"] ||
    chartData?.Biodiversity?.["Land where natural processes predominate by country"];
  const biodiversitySeries = pickSeries(biodiversityShareBlock);
  const biodiversityLastRow = getLastYearRow(biodiversitySeries);
  const biodiversityActual = asNumber(biodiversityLastRow?.computedShare);

  const biodiversityGlobalSeries = Array.isArray(biodiversityShareBlock)
    ? biodiversityShareBlock
    : Array.isArray(biodiversityShareBlock?.Global)
      ? biodiversityShareBlock.Global
      : Array.isArray(biodiversityShareBlock?.["Global"])
        ? biodiversityShareBlock["Global"]
        : null;
  const biodiversityGlobal2020Row = Array.isArray(biodiversityGlobalSeries)
    ? biodiversityGlobalSeries.find((row) => Number(row.year) === 2020)
    : null;
  const biodiversityGlobal2020ComputedShare = Number(biodiversityGlobal2020Row?.computedShare);
  const biodiversityTarget = Number.isFinite(biodiversityGlobal2020ComputedShare)
    ? biodiversityGlobal2020ComputedShare * 1.15
    : asNumber(biodiversityLastRow?.targetShare);

  const nitrogenBlock = chartData?.Nitrogen?.["Nitrogen Application"];
  const nitrogenSeries = pickSeries(nitrogenBlock);
  const nitrogen2050Row = Array.isArray(nitrogenSeries)
    ? nitrogenSeries.find((row) => Number(row.year) === 2050)
    : null;
  const nitrogenActual = nitrogen2050Row
    ? (asNumber(nitrogen2050Row.calcN_org) || 0) +
    (asNumber(nitrogen2050Row.calcN_synth) || 0)
    : null;
  const nitrogenTargetValue = 68000;

  const targetSummaryRows = [
    {
      category: "Food Security",
      metric: "Daily per capita food intake",
      actual:
        foodActual == null
          ? "N/A"
          : `${foodActual.toLocaleString("en-US", { maximumFractionDigits: 0 })} kcal/cap/day`,
      target:
        foodMinTarget == null || foodMaxTarget == null
          ? "N/A"
          : `${foodMinTarget.toLocaleString("en-US", { maximumFractionDigits: 0 })} - ${foodMaxTarget.toLocaleString("en-US", { maximumFractionDigits: 0 })} kcal/cap/day`,
      status: foodSecurityStatus,
      year: foodLastRow?.year ?? "-",
    },
    {
      category: "GHG",
      metric: "Net AFOLU (2050)",
      actual:
        ghgActual == null
          ? "N/A"
          : `${(ghgActual / 1000).toLocaleString("en-US", { maximumFractionDigits: 2 })} GT CO2e`,
      target:
        ghgTarget == null
          ? "N/A"
          : `<= ${(ghgTarget / 1000).toLocaleString("en-US", { maximumFractionDigits: 2 })} GT CO2e`,
      status:
        ghgActual != null && ghgTarget != null
          ? ghgActual <= ghgTarget
            ? "Achieved"
            : "Not achieved"
          : "No target defined",
      year: ghgLastRow?.year ?? "-",
    },
    {
      category: "GHG",
      metric: "Deforestation + Sequestration",
      actual:
        ghgDeforestationSequestrationGt == null
          ? "N/A"
          : `${ghgDeforestationSequestrationGt.toLocaleString("en-US", { maximumFractionDigits: 2 })} GT CO2e`,
      target:
        ghgDeforestationSequestrationGt == null
          ? "N/A"
          : "< -1.3 GT CO2e",
      status:
        ghgDeforestationSequestrationGt != null
          ? ghgDeforestationSequestrationGt < -1.3
            ? "Achieved"
            : "Not achieved"
          : "No target defined",
      year: ghgLastRow?.year ?? "-",
    },
    {
      category: "GHG",
      metric: "Cumulative Net AFOLU (2025-2050)",
      actual:
        ghgNetAfolu2025to2050Gt == null
          ? "N/A"
          : `${ghgNetAfolu2025to2050Gt.toLocaleString("en-US", { maximumFractionDigits: 2 })} GT CO2e`,
      target:
        ghgNetAfolu2025to2050Gt == null
          ? "N/A"
          : "< 40 GT CO2e",
      status:
        ghgNetAfolu2025to2050Gt != null
          ? ghgNetAfolu2025to2050Gt < 40
            ? "Achieved"
            : "Not achieved"
          : "No target defined",
      year: ghgLastRow?.year ?? "-",
    },
    {
      category: "Biodiversity",
      metric: "Protected area share (2050)",
      actual:
        protectedArea2050Share == null
          ? "N/A"
          : `${(protectedArea2050Share * 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`,
      target:
        protectedArea2050Share == null
          ? "N/A"
          : "> 30%",
      status:
        protectedArea2050Share != null
          ? protectedArea2050Share > 0.3
            ? "Achieved"
            : "Not achieved"
          : "No target defined",
      year: 2050,
    },
    {
      category: "Biodiversity",
      metric: "Share of land supporting biodiversity",
      actual:
        biodiversityActual == null
          ? "N/A"
          : `${(biodiversityActual * 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}%`,
      target:
        biodiversityTarget == null
          ? "N/A"
          : `>= ${(biodiversityTarget * 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}%`,
      status:
        biodiversityActual != null && biodiversityTarget != null
          ? biodiversityActual >= biodiversityTarget
            ? "Achieved"
            : "Not achieved"
          : "No target defined",
      year: biodiversityLastRow?.year ?? "-",
    },
    {
      category: "Nitrogen",
      metric: "Nitrogen Application (Organic + Synthetic) (2050)",
      actual:
        nitrogenActual == null
          ? "N/A"
          : `${nitrogenActual.toLocaleString("en-US", { maximumFractionDigits: 0 })} mt N`,
      target:
        nitrogenActual == null
          ? "N/A"
          : `< ${nitrogenTargetValue.toLocaleString("en-US", { maximumFractionDigits: 0 })} mt N`,
      status:
        nitrogenActual != null
          ? nitrogenActual < nitrogenTargetValue
            ? "Achieved"
            : "Not achieved"
          : "No target defined",
      year: nitrogen2050Row?.year ?? "-",
    },
  ];

  const renderTargetsContent = () => {
    const statusClasses = {
      Achieved: "bg-[#E7F6E4] text-[#2E7D32]",
      "Not achieved": "bg-[#FDECEA] text-[#C62828]",
      "No target defined": "bg-[#EEF1F4] text-[#5F6368]",
    };

    return (
      <div className="flex w-full flex-col gap-4">
        <h2 className="text-[18px] font-medium text-[#323334]">Targets Summary</h2>

        <div className="max-h-[520px] overflow-auto rounded-xl border border-[#E3E5E6]">
          <table className="w-full min-w-[760px] border-collapse text-left text-sm">
            <thead className="sticky top-0 z-10 bg-[#F8F9FA] text-[#323334] shadow-[0_1px_0_0_#E3E5E6]">
              <tr>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Indicator</th>
                <th className="px-4 py-3 font-medium">Actual</th>
                <th className="px-4 py-3 font-medium">Target</th>
                <th className="px-4 py-3 font-medium">Year</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>

            <tbody>
              {targetSummaryRows.map((row) => (
                <tr
                  key={`${row.category}-${row.metric}`}
                  className="border-t border-[#EDF0F2] text-[#5F6368] odd:bg-white even:bg-[#FBFCFD]"
                >
                  <td className="px-4 py-3 font-medium text-[#323334]">{row.category}</td>
                  <td className="px-4 py-3">{row.metric}</td>
                  <td className="px-4 py-3">{row.actual}</td>
                  <td className="px-4 py-3">{row.target}</td>
                  <td className="px-4 py-3">{row.year}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${statusClasses[row.status] || statusClasses["No target defined"]
                        }`}
                    >
                      {row.status}

                      {row.category === "Food Security" &&
                        row.status === "Not achieved" &&
                        foodSecurityFailures.length > 0 && (
                          <button
                            type="button"
                            onClick={showFoodSecurityFailuresModal}
                            className="ml-1 cursor-pointer hover:opacity-70"
                            title="View details of regions or countries not achieved"
                          >
                            🔍
                          </button>
                        )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeSubTab) {
      case "Targets":
        return renderTargetsContent();

      case "Food":
        return (
          <FoodTab
            data={chartData.Food ?? {}}
            pathway={pathwayName}
            pathwayId={pathwayName}
            scenathonId={scenathonId}
          />
        );

      case "Trade":
        return (
          <TradeTab
            data={chartData.Trade ?? {}}
            pathway={pathwayName}
            pathwayId={pathwayName}
            scenathonId={scenathonId}
          />
        );

      case "Land use":
        return (
          <LandUseTab
            data={chartData["Land use"] ?? {}}
            pathway={pathwayName}
            pathwayId={pathwayName}
            scenathonId={scenathonId}
          />
        );

      case "GHG":
        return (
          <GHGTab
            data={chartData.GHG ?? {}}
            pathway={pathwayName}
            pathwayId={pathwayName}
            scenathonId={scenathonId}
          />
        );

      case "Nitrogen":
        return (
          <NitrogenTab
            data={chartData.Nitrogen ?? {}}
            pathway={pathwayName}
            pathwayId={pathwayName}
            scenathonId={scenathonId}
          />
        );

      case "Biodiversity":
        return (
          <BiodiversityTab
            data={chartData.Biodiversity ?? {}}
            pathway={pathwayName}
            pathwayId={pathwayName}
            scenathonId={scenathonId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      <div className="border-b border-[#E3E5E6] px-4 md:px-6">
        <div className="overflow-x-auto [scrollbar-width:thin]">
          <div className="mx-auto flex w-max min-w-full items-center justify-start gap-2 p-0.5 text-sm font-medium text-[#101010] md:justify-center">
            {subTabs.map((tab) => {
              const isActive = activeSubTab === tab.key;

              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveSubTab(tab.key)}
                  className={`group relative overflow-hidden rounded-lg px-3 py-2 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54B046]/40 ${isActive
                    ? "bg-white text-[#101010] shadow-[0_1px_8px_rgba(16,16,16,0.08)]"
                    : "text-[#5F6368] hover:-translate-y-[1px] hover:bg-white hover:text-[#101010]"
                    }`}
                  aria-pressed={isActive}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span className="inline-flex items-center gap-2 whitespace-nowrap">
                    <span
                      className={`h-1.5 w-1.5 rounded-full transition-colors ${isActive ? "bg-[#54B046]" : "bg-[#C2C7CC] group-hover:bg-[#54B046]"
                        }`}
                    />
                    <span>{tab.label}</span>
                  </span>

                  <span
                    className={`absolute bottom-0 left-2 right-2 h-[2px] origin-center rounded-full bg-[#54B046] transition-all duration-300 ease-out ${isActive ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0"
                      }`}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white px-8 pb-6 pt-8">{renderTabContent()}</div>
    </div>
  );
}